/**
 * Aviso de privacidad leído desde el Perfil (006 · T051 · US5; contracts/staff-ui.md §5).
 *
 * Reutiliza el mismo texto de la activación (`PrivacyNoticeText` de
 * `features/activation/privacy-notice.tsx`, embebido en el build) y le suma la versión vigente y la
 * fecha en que el trabajador la aceptó, que llegan de `GET /profile` (la fecha se formatea en la
 * zona de la sede principal). "Volver al perfil" regresa a `/perfil`. Con la barra de pestañas y
 * Perfil activa (FR-001).
 */
import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { useOnlineStatus } from '../../api/online';
import { GenericErrorScreen } from '../../components/ui/generic-error';
import { OfflineState } from '../../components/ui/offline-state';
import { Screen } from '../../components/ui/screen';
import { Skeleton, SkeletonText } from '../../components/ui/skeleton';
import { TabBar } from '../../components/ui/tab-bar';
import { formatDate, t } from '../../i18n';
import { PrivacyNoticeText } from '../activation/privacy-notice';

import { useStaffProfile } from './api';
import { primaryTimezone } from './profile-screen';

const NAV = <TabBar active="profile" />;

function BackToProfile() {
  return (
    <Link
      to="/perfil"
      className="inline-flex min-h-11 items-center gap-fm-2 self-start font-ui font-semibold text-fm-primary-compact focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus"
    >
      <ArrowLeft aria-hidden="true" size={20} />
      {t('profile.notice.back')}
    </Link>
  );
}

/** Skeletons del aviso; también los usa la puerta de `routes/aviso-de-privacidad.tsx`. */
export function PrivacyNoticeLoading() {
  return (
    <Screen data-testid="privacy-notice-loading" title={t('profile.notice.title')} nav={NAV}>
      <SkeletonText label={t('common.states.loading')} className="gap-fm-3">
        <Skeleton radius="sm" className="h-5 w-40" />
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} radius="sm" className="h-4 w-full" />
        ))}
      </SkeletonText>
    </Screen>
  );
}

export function PrivacyNoticePage() {
  const online = useOnlineStatus();
  const profile = useStaffProfile();

  if (!online && profile.data === undefined) {
    return (
      <Screen title={t('profile.notice.title')} nav={NAV}>
        <OfflineState onRetry={() => void profile.refetch()} retrying={profile.isFetching} />
      </Screen>
    );
  }

  if (profile.isPending) {
    return <PrivacyNoticeLoading />;
  }

  if (profile.isError) {
    return <GenericErrorScreen nav={NAV} onRetry={() => void profile.refetch()} />;
  }

  const { version, accepted_at: acceptedAt } = profile.data.privacy_notice;
  const timeZone = primaryTimezone(profile.data);

  return (
    <Screen
      data-testid="privacy-notice-page"
      header={<BackToProfile />}
      title={t('profile.notice.title')}
      nav={NAV}
    >
      <p className="text-fm-sm text-fm-text-secondary">
        <span data-testid="privacy-notice-version">{t('profile.notice.version', { version })}</span>
        {acceptedAt ? (
          <>
            {' · '}
            <span data-testid="privacy-notice-accepted">
              {t('profile.notice.accepted', { date: formatDate(acceptedAt, timeZone) })}
            </span>
          </>
        ) : null}
      </p>
      <PrivacyNoticeText />
    </Screen>
  );
}
